export type StateCode = 'RS' | 'SC';
export type AssistancePlan = 'basic' | 'complete' | 'business';
export type CalculationMode = 'area' | 'manual';

export interface CalculatorInput {
  state: StateCode;
  occupationId: string;
  mode: CalculationMode;
  area: number;
  buildingValue: number | null;
  electrical: number;
  liability: number;
  glass: number;
  wind: boolean;
  pipes: boolean;
  glassEnabled: boolean;
  assistance: AssistancePlan;
}

interface Occupation {
  id: string;
  label: string;
  residential: boolean;
  rentCap: number;
  windCap: number;
  rates: number[];
}

interface Region {
  name: string;
  cub: number;
  floor: number;
  assistance: Record<AssistancePlan, number>;
  occupations: Occupation[];
}

export interface CoverageResult {
  id: string;
  label: string;
  insuredAmount: number;
  premium: number;
}

export interface CalculationResult {
  rulesVersion: string;
  state: StateCode;
  stateName: string;
  occupationId: string;
  occupationLabel: string;
  residential: boolean;
  buildingAmount: number;
  coverages: CoverageResult[];
  assistance: { id: AssistancePlan; label: string; premium: number };
  net: number;
  tax: number;
  total: number;
  specialAvailable: boolean;
}

export const RULES_VERSION = '2026-09-16.1';
const IOF = 0.0738;
const SPECIAL_THRESHOLD = 2500;

const rules: Record<StateCode, Region> = {
  SC: {
    name: 'Santa Catarina',
    cub: 2978,
    floor: 150000,
    assistance: { basic: 8.57, complete: 8.57, business: 8.75 },
    occupations: [
      {
        id: 'masonry',
        label: 'Residência Alvenaria',
        residential: true,
        rentCap: 200000,
        windCap: 250000,
        rates: [
          0.000300009, 0.000293403, 0.000300009, 0.004347, 0.0079212, 0.0004968,
          0.0156354, 0.0044574
        ]
      },
      {
        id: 'office',
        label: 'Escritório e Consultório',
        residential: false,
        rentCap: 200000,
        windCap: 93750,
        rates: [
          0.000449988, 0.000293403, 0.000449988, 0.0034362, 0.0079212, 0.002967,
          0.0177606, 0.0045954
        ]
      },
      {
        id: 'commerce',
        label: 'Comércio e Serviços',
        residential: false,
        rentCap: 80000,
        windCap: 93750,
        rates: [
          0.00102018, 0.00293412, 0.00102018, 0.00345, 0.0079212, 0.002967, 0.0177606,
          0.0045954
        ]
      }
    ]
  },
  RS: {
    name: 'Rio Grande do Sul',
    cub: 2679,
    floor: 70000,
    assistance: { basic: 24, complete: 48, business: 24 },
    occupations: [
      {
        id: 'apartment',
        label: 'Apartamento',
        residential: true,
        rentCap: 200000,
        windCap: 250000,
        rates: [
          0.0001344, 0.0001344, 0.0001611, 0.0010746, 0.0071292, 0.0004968, 0.0156354,
          0.0044574
        ]
      },
      {
        id: 'masonry',
        label: 'Residência Alvenaria',
        residential: true,
        rentCap: 200000,
        windCap: 250000,
        rates: [
          0.0003045, 0.0003045, 0.0003654, 0.0020148, 0.0079212, 0.0004968, 0.0156354,
          0.0044574
        ]
      },
      {
        id: 'wood',
        label: 'Residência Madeira',
        residential: true,
        rentCap: 37500,
        windCap: 37500,
        rates: [
          0.0012006, 0.0012006, 0.0012006, 0.006324, 0.02484, 0.0004968, 0.0156354,
          0.005796
        ]
      },
      {
        id: 'office',
        label: 'Escritório e Consultório',
        residential: false,
        rentCap: 200000,
        windCap: 93750,
        rates: [
          0.0002031, 0.0002031, 0.0002436, 0.0013431, 0.0079212, 0.002967, 0.0177606,
          0.0045954
        ]
      },
      {
        id: 'commerce',
        label: 'Comércio e Serviços',
        residential: false,
        rentCap: 80000,
        windCap: 93750,
        rates: [
          0.000705, 0.000705, 0.000846, 0.0020148, 0.0079212, 0.002967, 0.0177606,
          0.0045954
        ]
      }
    ]
  }
};

const planNames: Record<AssistancePlan, string> = {
  basic: 'Básico',
  complete: 'Completo',
  business: 'Empresa'
};

const coverageNames = [
  'Incêndio - prédio',
  'Incêndio - conteúdo',
  'Perda de aluguel',
  'Vendaval e granizo',
  'Danos elétricos',
  'Responsabilidade civil',
  'Quebra de vidros',
  'Tubulações'
];

function getContext(input: Pick<CalculatorInput, 'state' | 'occupationId'>) {
  const region = rules[input.state];
  if (!region) throw new Error('Estado inválido.');
  const occupation = region.occupations.find(item => item.id === input.occupationId);
  if (!occupation) throw new Error('Tipo de imóvel inválido para este estado.');
  return { region, occupation, first: region.occupations[0].id === occupation.id };
}

function validNumber(
  value: number,
  label: string,
  min = 0,
  max = Number.MAX_SAFE_INTEGER
) {
  if (typeof value !== 'number' || !isFinite(value) || value < min || value > max) {
    throw new Error(`${label}: valor fora dos limites permitidos.`);
  }
  return value;
}

function buildingAmount(input: CalculatorInput) {
  const { region } = getContext(input);
  if (input.mode === 'manual')
    return validNumber(input.buildingValue as number, 'Valor do prédio', 1);
  if (input.mode !== 'area') throw new Error('Modo de cálculo inválido.');
  validNumber(input.area, 'Área', 1);
  if (!Number.isInteger(input.area))
    throw new Error('Informe a área em metros quadrados inteiros.');
  return validNumber(Math.max(input.area * region.cub, region.floor), 'Valor do prédio');
}

export const getRegion = (state: StateCode) => rules[state];

export const defaults = (state: StateCode, occupationId?: string): CalculatorInput => {
  const region = rules[state];
  const occupation =
    region.occupations.find(item => item.id === occupationId) || region.occupations[0];
  return {
    state,
    occupationId: occupation.id,
    mode: 'area',
    area: 100,
    buildingValue: null,
    electrical: 3000,
    liability: 20000,
    glass: 2000,
    wind: occupation.id !== 'apartment',
    pipes: false,
    glassEnabled: false,
    assistance: occupation.residential ? 'complete' : 'business'
  };
};

export const changeOccupation = (
  input: CalculatorInput,
  occupationId: string
): CalculatorInput => {
  const { occupation } = getContext({ state: input.state, occupationId });
  return {
    ...input,
    occupationId,
    wind: occupation.id !== 'apartment',
    assistance: occupation.residential ? 'complete' : 'business'
  };
};

export const calculate = (input: CalculatorInput): CalculationResult => {
  const { region, occupation, first } = getContext(input);
  const building = buildingAmount(input);
  const electricalMin = building > 300000 ? 4500 : 3000;
  const electricalMax = first ? 125000 : 62500;
  const liabilityMin = building > 300000 ? 40000 : 20000;
  const glassMax = first ? 100000 : 150000;
  const electrical = validNumber(
    input.electrical,
    'Danos elétricos',
    electricalMin,
    electricalMax
  );
  const liability = occupation.residential
    ? validNumber(input.liability, 'Responsabilidade civil', liabilityMin, 100000)
    : 0;
  const glass = input.glassEnabled
    ? validNumber(input.glass, 'Vidros', 2000, glassMax)
    : 0;
  if (
    typeof input.wind !== 'boolean' ||
    typeof input.pipes !== 'boolean' ||
    typeof input.glassEnabled !== 'boolean'
  ) {
    throw new Error('Seleção inválida.');
  }
  if (!Object.prototype.hasOwnProperty.call(region.assistance, input.assistance)) {
    throw new Error('Escolha um plano de assistência.');
  }

  const amounts = [
    building,
    input.state === 'SC' ? 80000 : building * 0.1,
    Math.min(building * 0.1, occupation.rentCap),
    Math.min(building * 0.1, occupation.windCap),
    electrical,
    liability,
    glass,
    Math.min(building * 0.1, 50000)
  ];
  const active = [
    true,
    true,
    true,
    input.wind,
    true,
    occupation.residential,
    input.glassEnabled,
    input.pipes
  ];
  const coverages = coverageNames.reduce<CoverageResult[]>((result, name, index) => {
    if (!active[index]) return result;
    const id = [
      'building',
      'contents',
      'rent',
      'wind',
      'electrical',
      'liability',
      'glass',
      'pipes'
    ][index];
    result.push({
      id,
      label: id === 'liability' && input.state === 'SC' ? 'RC familiar' : name,
      insuredAmount: amounts[index],
      premium: (amounts[index] * occupation.rates[index]) / 0.3
    });
    return result;
  }, []);
  const assistance = {
    id: input.assistance,
    label: planNames[input.assistance],
    premium: region.assistance[input.assistance]
  };
  const net = coverages.reduce((sum, item) => sum + item.premium, 0) + assistance.premium;
  const tax = net * IOF;
  const total = net + tax;
  validNumber(total, 'Total');
  return {
    rulesVersion: RULES_VERSION,
    state: input.state,
    stateName: region.name,
    occupationId: occupation.id,
    occupationLabel: occupation.label,
    residential: occupation.residential,
    buildingAmount: building,
    coverages,
    assistance,
    net,
    tax,
    total,
    specialAvailable: total > SPECIAL_THRESHOLD
  };
};
